import "../styles/template-picker.scss";
import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import * as DOMPurify from "dompurify";
import { GuiSelect } from "./global-ui/gui-select";
import { useUpdateState } from "../utilities/utilities";
import actions from "../actions/actions";
import { Modal } from "./modal";
import { GuiButton } from "./global-ui/gui-button";
import { Icon } from "../components/icon";
import { TemplateType } from "../utilities/constants";

// When voiceCombined is true, this picker is used for the "Voice" message
// type, which covers two distinct template flavors: pre-recorded audio file
// templates (TemplateType.VOICE) and text-to-speech templates
// (TemplateType.TTS). Both are loaded together, grouped in the dropdown, and
// picking one attaches the data the caller needs to react appropriately
// (see SendAlert.useTemplate).
export function TemplatePickerModal({
  alertType,
  voiceCombined,
  onInsert,
  onCancel,
}) {
  const effectiveAlertType = alertType == null ? 3 : alertType;
  let templates = useSelector((store) => store.alert.templates);
  let voiceTemplates = useSelector((store) => store.alert.voiceTemplates);
  let [state, updateState] = useUpdateState({});

  useEffect(() => {
    if (voiceCombined) {
      actions.alerts.loadVoiceTemplates();
    } else {
      actions.alerts.loadTemplates(effectiveAlertType);
    }
  }, [alertType, voiceCombined]);

  const options = voiceCombined
    ? [
        {
          label: "Text to Speech",
          options: voiceTemplates.filter(
            (t) => t.templateType === TemplateType.TTS,
          ),
        },
        {
          label: "Voice Recording",
          options: voiceTemplates.filter(
            (t) => t.templateType === TemplateType.VOICE,
          ),
        },
      ]
    : templates;

  async function update(name, item) {
    updateState({
      template: item,
      preview: item?.message,
      audioPreviewUrl: null,
      loadingAudio: false,
    });

    if (voiceCombined && item?.templateType === TemplateType.VOICE) {
      updateState({ loadingAudio: true });
      let result = await actions.alerts.getTemplateById(item.value);
      // voiceAudioUrl is a playable URL (blob URL or embedded data: URL)
      // for previewing only — it's not necessarily a valid value to submit
      // back to the backend for a new alert (e.g. large data: URLs).
      let audioUrl = result?.voiceAudioUrl ?? result?.VoiceAudioUrl ?? null;
      let fileName = result?.voiceFileName ?? result?.VoiceFileName ?? null;
      let rawTemplate = result?.template ?? result?.Template ?? "";

      // Determine what should actually be submitted with the alert: a real
      // blob-storage URL can be reused directly, otherwise this is a
      // CDYNE-recorded file identified by a soundFileId (possibly wrapped in
      // a legacy blobsync URL) that the backend resolves at send time.
      let isBlobUrl =
        typeof rawTemplate === "string" &&
        rawTemplate.startsWith("https://") &&
        rawTemplate.includes("blob.core.windows.net");
      let fileIdentifier = null;
      if (!isBlobUrl && rawTemplate) {
        try {
          let url = new URL(rawTemplate);
          fileIdentifier = url.searchParams.get("vFileId") || rawTemplate;
        } catch (_) {
          fileIdentifier = rawTemplate;
        }
      }

      updateState({
        loadingAudio: false,
        audioPreviewUrl: audioUrl,
        template: {
          ...item,
          voiceAudioUrl: audioUrl,
          voiceFileName: fileName,
          voiceFileUrl: isBlobUrl ? rawTemplate : null,
          voiceFileIdentifier: isBlobUrl ? null : fileIdentifier,
        },
      });
    }
  }

  const isVoiceRecordingSelected =
    voiceCombined && state?.template?.templateType === TemplateType.VOICE;

  return (
    <Modal className="template-picker-modal" onClose={onCancel}>
      <div className="template-picker">
        <div className="template-picker-header">
          <h2>Message Templates</h2>
          <Icon icon="times" className="close" onClick={onCancel} />
        </div>
        <GuiSelect
          label="Template"
          name="template"
          value={state.template?.value}
          options={options}
          onChange={update}
          inModal={true}
        />

        {isVoiceRecordingSelected ? (
          <div className="preview">
            {state?.loadingAudio && <span>Loading audio…</span>}
            {!state?.loadingAudio && state?.audioPreviewUrl && (
              <audio controls src={state.audioPreviewUrl} style={{ width: "100%" }} />
            )}
            {!state?.loadingAudio && !state?.audioPreviewUrl && (
              <span>No audio preview available.</span>
            )}
          </div>
        ) : (
          <div
            className="preview"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(state?.preview),
            }}
          ></div>
        )}

        <div className="button-container">
          <GuiButton className="btn-outline" onClick={onCancel}>
            Cancel
          </GuiButton>
          <GuiButton
            className="insert"
            disabled={state?.loadingAudio}
            onClick={() => onInsert(state?.template)}
          >
            Insert
          </GuiButton>
        </div>
      </div>
    </Modal>
  );
}
